import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import {
  INVOICE_MODE_OPTIONS,
  WEEKLY_INVOICE_DAY_OPTIONS,
  formatCompanyActive,
  invoiceModeLabel,
  weeklyInvoiceDayLabel,
} from 'src/constants/distributor-invoicing';
import { AlertService } from 'src/core/services/alert.service';
import { CompanyService } from 'src/core/services/company.service';
import { LoaderService } from 'src/core/services/loader.service';
import { SageService } from 'src/core/services/sage.service';
import { PermissionsService } from 'src/core/services/permissions.service';
import { all_countries, Country } from 'src/form/components/data/countries';
import { all_provinces } from 'src/form/components/data/provinces';
import { Address, AddressEditPayload } from 'src/interfaces/address';
import { Company, CompanyType, EditCompanyPayload } from 'src/interfaces/company';
import { environment } from 'src/environments/environment';
import { resolveLinkId } from 'src/core/utils/entity-link-ids';
import { isDeliveryFeeExemptFlag } from 'src/core/utils/delivery-fee';
import {
  isBlankSageCustomerCode,
  readSageCustomerCode,
  SAGE_CUSTOMER_CODE_MISSING_WARNING,
  sageCustomerCodeForApi,
} from 'src/core/utils/sage-customer-code';

@Component({
    selector: 'app-company-view',
    templateUrl: './company-view.component.html',
    styleUrls: ['./company-view.component.scss'],
    standalone: false
})
export class CompanyViewComponent implements OnInit {

  companyTypes: CompanyType[] = [];
  company: Company | null = null;
  selectedDistributor: Company | null = null;
  /** User cleared distributor in UI — save must send distributor_id: null. */
  distributorCleared = false;
  showDistributor: boolean = false;
  address: Address | null = null;

  readonly invoiceModeOptions = INVOICE_MODE_OPTIONS;
  readonly weeklyInvoiceDayOptions = WEEKLY_INVOICE_DAY_OPTIONS;

  filteredCountriesForIndividual: Observable<Country[]>;

  countries = all_countries;
  provinces = all_provinces;

  isLoading: boolean = false;

  companyDetailsForm = this._formBuilder.group({
    companyName: ['', Validators.required],
    email: ['', Validators.compose([Validators.required, Validators.email])],
    emailAlt: ['', Validators.email],
    phoneNumber: ['', Validators.compose([Validators.required, Validators.pattern('^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-\s\./0-9]*$')])],
    phoneNumberAlt: ['', Validators.pattern('^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-\s\./0-9]*$')],
    practiceNumber: ['', Validators.required],
    vatRegistrationNumber: ['', Validators.pattern(/[0-9]+/)],
    sageCustomerCode: [''],
    companyType: ['', Validators.required],
    distributor: [''],
    invoiceMode: [''],
    weeklyInvoiceDay: [''],
    isActive: [true],
    deliveryFeeExempt: [false],
  });

  addressDetailsForm = this._formBuilder.group({
    country: ['South Africa', Validators.required],
    province: ['', Validators.required],
    city: [''],
    addressLine1: ['', Validators.required],
    addressLine2: ['', Validators.required],
    buildingAptNumber: ['', Validators.required],
    postcode: ['', Validators.compose([Validators.required, Validators.pattern(/^[0-9]{4,5}$/)])]
  });

  editMode: boolean = false;
  addingToSage: boolean = false;
  /** Hide Sage row / Add to Sage when disabled in environment (e.g. prod). */
  readonly sageUiEnabled = environment.sageUiEnabled;

  constructor(
    private _formBuilder: UntypedFormBuilder,
    private _companyService: CompanyService,
    private _router: Router,
    private _route: ActivatedRoute,
    private _loaderService: LoaderService,
    private _sageService: SageService,
    public readonly perm: PermissionsService,
    private _alertService: AlertService,
  ) { }

  ngOnInit(): void {
    this._route
      .data
      .subscribe(data => {
        this.company = this._companyService.applyNotedSageCustomerCode(data.company.company);
        this.companyTypes = data.company.companyTypes;
        if (this.company) this.updateView(this.company);
      })

    this
      ._loaderService
      .isLoading
      .subscribe(isLoading => {
        this.isLoading = isLoading;
      });

    this.filteredCountriesForIndividual = this
      .addressDetailsForm
      .controls['country']
      .valueChanges
      .pipe(
        startWith('South Africa'),
        map(value => this._filterCountries(value))
      );


    this.companyDetailsForm
      .controls['companyType']
      .valueChanges
      .subscribe(value => {
        // choose to show or not show distributor field
        const companyType: CompanyType = value;
        if (companyType) this.toggleDistributor(companyType);
      });

    this.companyDetailsForm.controls['invoiceMode'].valueChanges.subscribe(() => {
      this.updateWeeklyInvoiceDayValidators();
    });

  }

  /** Current row is a distributor (DIS). */
  isCompanyDistributor(): boolean {
    if (!this.company) return false;
    const t = this.companyTypes.find((x) => x.id === this.company!.company_type_id);
    return t?.code === 'DIS';
  }

  get companyTypeLabel(): string {
    if (!this.company) {
      return '';
    }
    const type = this.companyTypes.find((x) => x.id === this.company!.company_type_id);
    return type?.type ?? '';
  }

  /** Edit form: company type control is DIS (user may have changed type). */
  isEditingDistributorType(): boolean {
    const t = this.companyDetailsForm.get('companyType')?.value as CompanyType | null;
    return t?.code === 'DIS';
  }

  /** Whether Save should be enabled. Active, delivery-fee, and Sage-code updates skip full validation. */
  get canSave(): boolean {
    if (this.usesRelaxedSaveRules()) {
      return !!this.company;
    }
    const hasDistributor =
      !this.showDistributor ||
      this.distributorCleared ||
      !!this.selectedDistributor ||
      !!this.company?.distributor_id;
    return (
      this.companyDetailsForm.valid &&
      this.addressDetailsForm.valid &&
      hasDistributor
    );
  }

  /** Saving with Active off — allow incomplete records to be marked inactive. */
  private isSavingAsInactive(): boolean {
    return !this.companyDetailsForm.value.isActive;
  }

  /** Skip distributor, address, and required-field checks. */
  private usesRelaxedSaveRules(): boolean {
    return (
      this.isSavingAsInactive() ||
      this.hasActiveStatusChanged() ||
      this.hasDeliveryFeeExemptChanged() ||
      this.hasSageCustomerCodeChanged()
    );
  }

  private pickString(formVal: unknown, fallback: string | null | undefined): string {
    const v = formVal != null ? String(formVal).trim() : '';
    return v || fallback || '';
  }

  private resolveCompanyType(): CompanyType | undefined {
    const formType = this.companyDetailsForm.get('companyType')?.value as CompanyType | null;
    if (formType?.id != null) {
      return formType;
    }
    if (this.company?.company_type_id == null) {
      return undefined;
    }
    return this.companyTypes.find((t) => t.id === this.company!.company_type_id);
  }

  private isCompanyActiveValue(
    isActive: boolean | number | string | null | undefined
  ): boolean {
    return !(
      isActive === false ||
      isActive === 0 ||
      String(isActive) === '0'
    );
  }

  private hasActiveStatusChanged(): boolean {
    if (!this.company) {
      return false;
    }
    const formActive = !!this.companyDetailsForm.value.isActive;
    const recordActive = this.isCompanyActiveValue(this.company.is_active);
    return formActive !== recordActive;
  }

  private hasDeliveryFeeExemptChanged(): boolean {
    if (!this.company) {
      return false;
    }
    const formExempt = !!this.companyDetailsForm.value.deliveryFeeExempt;
    const recordExempt = isDeliveryFeeExemptFlag(this.company.delivery_fee_exempt);
    return formExempt !== recordExempt;
  }

  /** Sage customer code is optional, so a code-only edit must not wait on the rest of the form. */
  private hasSageCustomerCodeChanged(): boolean {
    if (!this.company) {
      return false;
    }
    const formCode = sageCustomerCodeForApi(this.companyDetailsForm.get('sageCustomerCode')?.value);
    return formCode !== readSageCustomerCode(this.company);
  }

  toggleDistributor(companyType: CompanyType) {

    if (companyType.code === 'DIS') {
      this.showDistributor = false;
      this.selectedDistributor = null;
      this.distributorCleared = false;
      this.companyDetailsForm.patchValue({ distributor: null });
      this.companyDetailsForm.controls['invoiceMode'].setValidators([Validators.required]);
      this.updateWeeklyInvoiceDayValidators();
    } else {
      this.showDistributor = true;
      this.companyDetailsForm.controls['invoiceMode'].clearValidators();
      this.companyDetailsForm.controls['weeklyInvoiceDay'].clearValidators();
      this.companyDetailsForm.patchValue({
        invoiceMode: '',
        weeklyInvoiceDay: '',
      });
    }
    this.companyDetailsForm.controls['invoiceMode'].updateValueAndValidity({ emitEvent: false });
    this.companyDetailsForm.controls['weeklyInvoiceDay'].updateValueAndValidity({ emitEvent: false });

  }

  private updateWeeklyInvoiceDayValidators(): void {
    const mode = this.companyDetailsForm.controls['invoiceMode'].value;
    const wk = this.companyDetailsForm.controls['weeklyInvoiceDay'];
    if (mode === 'GROUP_WEEKLY') {
      wk.setValidators([Validators.required]);
    } else {
      wk.clearValidators();
      wk.setValue('', { emitEvent: false });
    }
    wk.updateValueAndValidity({ emitEvent: false });
  }

  async updateView(company: Company): Promise<void> {
    // get company type

    const companyType: CompanyType | undefined = this.companyTypes
      .find(type => type.id === company.company_type_id);

    if (companyType) this.toggleDistributor(companyType);

    this.address = company.addresses[0];

    if (this.address) {
      this.addressDetailsForm.patchValue({
        country: this.address.country,
        province: this.address.province,
        city: this.address.city,
        addressLine1: this.address.address_1,
        addressLine2: this.address.address_2,
        buildingAptNumber: this.address.building_apt_number,
        postcode: this.address.postcode
      })
    }

    this.companyDetailsForm.patchValue({
      companyName: company.name,
      email: company.email,
      emailAlt: company.email_alt,
      phoneNumber: company.phone_number,
      phoneNumberAlt: company.phone_number_alt,
      practiceNumber: company.practice_number,
      vatRegistrationNumber: company.vat_registration_number,
      sageCustomerCode: readSageCustomerCode(company),
      companyType: companyType ? companyType : null,
      invoiceMode: company.invoice_mode || '',
      weeklyInvoiceDay: company.weekly_invoice_day || '',
      isActive: this.isCompanyActiveValue(company.is_active),
      deliveryFeeExempt: isDeliveryFeeExemptFlag(company.delivery_fee_exempt),
    });

    this.updateWeeklyInvoiceDayValidators();

    await this.hydrateDistributor(company);
  }

  /** Load distributor for save validation when API returns distributor_id without nested distributor. */
  private async hydrateDistributor(company: Company): Promise<void> {
    this.distributorCleared = false;
    if (company.distributor) {
      this.selectedDistributor = company.distributor;
      return;
    }
    if (company.distributor_id) {
      this.selectedDistributor = await this._companyService.getCompany(company.distributor_id);
      return;
    }
    this.selectedDistributor = null;
  }

  private buildAddressPayload(): AddressEditPayload | undefined {
    if (this.addressDetailsForm.valid) {
      return {
        building_apt_number: this.addressDetailsForm.value.buildingAptNumber,
        address_1: this.addressDetailsForm.value.addressLine1,
        address_2: this.addressDetailsForm.value.addressLine2,
        province: this.addressDetailsForm.value.province,
        city: this.addressDetailsForm.value.city,
        postcode: this.addressDetailsForm.value.postcode,
        country: this.addressDetailsForm.value.country,
        id: this.address ? this.address.id : undefined,
      };
    }
    if (this.address) {
      return {
        building_apt_number: this.address.building_apt_number,
        address_1: this.address.address_1,
        address_2: this.address.address_2,
        province: this.address.province,
        city: this.address.city,
        postcode: this.address.postcode,
        country: this.address.country,
        id: this.address.id,
      };
    }
    return undefined;
  }

  private resolveDistributorId(isDis: boolean): number | null | undefined {
    if (isDis) {
      return undefined;
    }
    return resolveLinkId(
      this.selectedDistributor?.id ?? this.company?.distributor_id,
      this.distributorCleared
    );
  }

  async save() {

    if (!this.company?.id) return;

    const relaxed = this.usesRelaxedSaveRules();
    const companyType = this.resolveCompanyType();
    const isDis = companyType?.code === 'DIS';
    const distributorId = this.resolveDistributorId(!!isDis);
    const companyTypeId = companyType?.id ?? this.company.company_type_id;

    // undefined = missing (require pick); null = user cleared (allowed).
    if (!relaxed && !isDis && distributorId === undefined) {
      this._alertService.errorAlert('Please select a distributor.');
      return;
    }

    if (!relaxed && isDis) {
      const mode = this.companyDetailsForm.value.invoiceMode;
      if (!mode) {
        this._alertService.errorAlert('Please select an invoice mode.');
        return;
      }
      if (mode === 'GROUP_WEEKLY' && !this.companyDetailsForm.value.weeklyInvoiceDay) {
        this._alertService.errorAlert('Please select a weekly invoice day.');
        return;
      }
    }

    if (!relaxed && !companyTypeId) {
      return;
    }

    const c = this.company;
    const f = this.companyDetailsForm.value;
    const addressPayload = this.buildAddressPayload();
    const sageCustomerCode = sageCustomerCodeForApi(
      this.companyDetailsForm.get('sageCustomerCode')?.value
    );
    const payload: EditCompanyPayload = {
      id: undefined,
      name: this.pickString(f.companyName, c.name),
      email: this.pickString(f.email, c.email),
      email_alt: this.pickString(f.emailAlt, c.email_alt),
      phone_number: this.pickString(f.phoneNumber, c.phone_number),
      phone_number_alt: this.pickString(f.phoneNumberAlt, c.phone_number_alt),
      practice_number: this.pickString(f.practiceNumber, c.practice_number),
      vat_registration_number: this.pickString(f.vatRegistrationNumber, c.vat_registration_number),
      sage_customer_code: sageCustomerCode,
      company_type_id: companyTypeId,
      primary_contact_id: c.primary_contact_id,
      address: addressPayload,
      distributor_id: isDis ? undefined : distributorId,
      is_active: !!f.isActive,
      delivery_fee_exempt: !!f.deliveryFeeExempt,
      ...(isDis
        ? {
            invoice_mode: (f.invoiceMode || c.invoice_mode || null) as EditCompanyPayload['invoice_mode'],
            weekly_invoice_day:
              (f.invoiceMode || c.invoice_mode) === 'GROUP_WEEKLY'
                ? (f.weeklyInvoiceDay || c.weekly_invoice_day || null)
                : null,
          }
        : {}),
    }

    const success: boolean = await this._companyService
      .editCompany(this.company.id, payload);

    if (success) {
      this._companyService.noteSageCustomerCode(this.company.id, sageCustomerCode);
      const fresh = await this._companyService.getCompany(this.company.id);
      const next = this._companyService.applyNotedSageCustomerCode(fresh ?? this.company);
      if (next) {
        this.company = next;
        this.updateView(next);
      }
    }
  }


  _filterCountries(value: string) {
    return this
      .countries
      .filter((country) => {
        return country
          .name
          .toLowerCase()
          .includes(
            value.toLowerCase()
          )
      })
  }

  async openSearchDistributorDialog() {
    const distributor = await this._companyService
      .openCompanySearchDialog("Search for a distributor", "", "DIS");

    if (!distributor) return;
    this.selectedDistributor = distributor;
    this.distributorCleared = false;
  }

  deselectDistributor() {

    this.selectedDistributor = null;
    this.distributorCleared = true;
  }

  readonly sageCustomerCodeMissingWarning = SAGE_CUSTOMER_CODE_MISSING_WARNING;

  missingSageCustomerCode(): boolean {
    if (this.editMode) {
      return isBlankSageCustomerCode(this.companyDetailsForm.get('sageCustomerCode')?.value);
    }
    return isBlankSageCustomerCode(readSageCustomerCode(this.company));
  }

  sageCustomerCodeLabel(): string {
    return readSageCustomerCode(this.company);
  }

  /** Whether this company is linked to a Sage contact (show "On Sage"). */
  get isOnSage(): boolean {
    return !!(this.company?.sage_contact_id);
  }

  /** Add this company as a Sage contact (POST /sage/contacts). Refreshes company; keeps sage id if GET omits it. */
  async addToSage(): Promise<void> {
    if (!this.company || this.addingToSage) return;
    this.addingToSage = true;
    let createdSageId: string | undefined;
    try {
      const result = await this._sageService.createContactForCompany(this.company);
      if (result?.success) {
        createdSageId = result.sage_contact_id;
        if (createdSageId) {
          this.company = { ...this.company!, sage_contact_id: createdSageId };
        }
      }
      const updated = await this._companyService.getCompany(this.company!.id!);
      if (updated) {
        const merged =
          updated.sage_contact_id?.trim() ||
          createdSageId ||
          this.company?.sage_contact_id;
        this.company =
          merged != null && String(merged).trim()
            ? { ...updated, sage_contact_id: String(merged) }
            : updated;
      } else if (createdSageId) {
        this.company = { ...this.company!, sage_contact_id: createdSageId };
      }
    } finally {
      this.addingToSage = false;
    }
  }

  // Navigate back to company list preserving filters
  backToList() {
    const qp = this._route.snapshot.queryParamMap;
    const search = qp.get('search') || undefined;
    const companyType = qp.get('companyType') || undefined;
    const isActive = qp.get('isActive') || undefined;
    this._router.navigate(['/portal/companies'], {
      queryParams: { search, companyType, isActive }
    });
  }

  distributorInvoiceModeDisplay(): string {
    return invoiceModeLabel(this.company?.invoice_mode);
  }

  distributorWeeklyDayDisplay(): string {
    return weeklyInvoiceDayLabel(this.company?.weekly_invoice_day);
  }

  companyActiveDisplay(): string {
    return formatCompanyActive(this.company?.is_active);
  }

}
